import type { TaskActivity } from '@portfolio-pm/shared';
import { Edit3, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { formatShortDate } from '../task-view-dates.ts';

/**
 * Notes and activity history under the task detail inspector.
 * Extracted from TaskDetailPanel.tsx.
 */
export function TaskActivityThread({
  activities,
  note,
  noteId,
  onNoteChange,
  onSubmit,
  onDeleteActivity,
  onUpdateActivity,
}: {
  activities: TaskActivity[];
  note: string;
  noteId?: string;
  onNoteChange(value: string): void;
  onSubmit(event: FormEvent<HTMLFormElement>): void;
  onDeleteActivity(id: string): void;
  onUpdateActivity(id: string, message: string): void;
}): JSX.Element {
  return (
    <div className="pane-divider-t">
      <form
        onSubmit={onSubmit}
        className="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-2 px-2 py-2"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded bg-[color:var(--app-accent-soft)] text-xs font-semibold text-[color:var(--app-accent-text)]">
          PM
        </div>
        <input
          id={noteId}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder="Add a note..."
          className="h-8 w-full rounded border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] px-2 text-sm outline-none placeholder:text-[color:var(--app-text-soft)] focus:border-[color:var(--app-border-strong)]"
        />
        <button type="submit" className="sr-only">
          Add note
        </button>
      </form>
      {activities.length > 0 && (
        <ul className="pane-divider-t pane-divide-y">
          {activities.map((activity) => (
            <TaskActivityItem
              key={activity.id}
              activity={activity}
              onDelete={() => onDeleteActivity(activity.id)}
              onUpdate={(message) => onUpdateActivity(activity.id, message)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskActivityItem({
  activity,
  onDelete,
  onUpdate,
}: {
  activity: TaskActivity;
  onDelete(): void;
  onUpdate(message: string): void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(activity.message);
  const [hovered, setHovered] = useState(false);

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== activity.message) onUpdate(trimmed);
    setEditing(false);
  };

  if (editing) {
    return (
      <li className="grid grid-cols-[2rem_minmax(0,1fr)] gap-2 px-2 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-[color:var(--app-accent-soft)] text-xs font-semibold text-[color:var(--app-accent-text)]">
          PM
        </div>
        <div className="min-w-0">
          <textarea
            // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commit();
              }
              if (e.key === 'Escape') {
                setDraft(activity.message);
                setEditing(false);
              }
            }}
            className="w-full rounded border border-[color:var(--app-border-strong)] bg-[color:var(--app-input-bg)] px-2 py-1 text-sm leading-5 text-[color:var(--app-text)] outline-none"
            rows={3}
          />
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={commit}
              className="rounded bg-[color:var(--app-accent)] px-2 py-0.5 text-xs font-medium text-white"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(activity.message);
                setEditing(false);
              }}
              className="text-xs text-[color:var(--app-text-soft)] hover:text-[color:var(--app-text)]"
            >
              Cancel
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li
      className="group grid grid-cols-[2rem_minmax(0,1fr)] gap-2 px-2 py-3"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded bg-[color:var(--app-accent-soft)] text-xs font-semibold text-[color:var(--app-accent-text)]">
        PM
      </div>
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-1">
          <p className="whitespace-pre-wrap text-sm leading-5 text-[color:var(--app-text)]">
            {activity.message}
          </p>
          {hovered && (
            <div className="ml-1 flex shrink-0 items-center gap-1">
              <button
                type="button"
                aria-label="Edit note"
                onClick={() => setEditing(true)}
                className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--app-text-soft)] hover:bg-[color:var(--app-surface-muted)] hover:text-[color:var(--app-text)]"
              >
                <Edit3 aria-hidden="true" size={13} />
              </button>
              <button
                type="button"
                aria-label="Delete note"
                onClick={onDelete}
                className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--app-text-soft)] hover:bg-[color:var(--app-danger-soft)] hover:text-[color:var(--app-danger)]"
              >
                <Trash2 aria-hidden="true" size={13} />
              </button>
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-[color:var(--app-text-soft)]">
          {formatShortDate(activity.createdAt)}
        </p>
      </div>
    </li>
  );
}
